import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PhoneCall, PhonePlus, PhoneSlash } from "@phosphor-icons/react";
import CallLogDialog from "@/components/CallLogDialog";

/**
 * Firm-scoped Call Logs dialog. Opens from the Firm 360 header AND from the
 * Dashboard row's quick-action phone icon. Shows the same 4-column table as
 * the global Activity feed (Time / Logged by / Remark / Duration) plus a
 * top-right "Log a call" button that pops the unified CallLogDialog with the
 * firm pinned and the type locked to "call".
 */
export default function FirmCallLogsDialog({ open, onClose, firm, refreshKey = 0, onCountChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [version, setVersion] = useState(0);

  const load = () => {
    if (!firm?.subdomain) return;
    setLoading(true);
    api
      .get(`/firms/${firm.subdomain}/call-logs`, { params: { page_size: 200 } })
      .then((r) => {
        setRows(r.data.rows || []);
        const count = r.data.total || 0;
        setTotal(count);
        if (onCountChange) onCountChange(count);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !firm?.subdomain) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firm?.subdomain, refreshKey, version]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="firm360-call-logs-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 pr-8">
              <div className="min-w-0">
                <DialogTitle className="font-display flex items-center gap-2">
                  <PhoneCall size={18} className="text-gray-700" />
                  Call Logs · {firm?.firm_name}
                </DialogTitle>
                <div className="text-xs text-gray-500 mt-0.5">
                  {loading ? "Loading…" : `${total} call${total === 1 ? "" : "s"} logged for this firm`}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setShowLog(true)}
                className="shrink-0 bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="firm360-call-logs-add-btn"
              >
                <PhonePlus size={14} weight="fill" className="mr-1.5" />
                Log a call
              </Button>
            </div>
            <DialogDescription className="sr-only">
              Call logs for {firm?.firm_name}. Click "Log a call" to add a new one.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mr-2 pr-2 mt-2">
            {!loading && rows.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-400">
                No calls have been logged for this firm yet. Click "Log a call" above to add the first one.
              </div>
            )}
            {!loading && rows.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left sticky top-0 z-10">
                    <tr>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-44">Time</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-40">Logged by</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-20 text-center">Outcome</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200">Remark</th>
                      <th className="h-9 px-3 text-[10px] uppercase tracking-wider font-medium text-gray-500 border-b border-gray-200 w-20 text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <FirmEntryRow
                        key={r.log_id}
                        time={r.called_at}
                        loggedByName={r.logged_by_name}
                        loggedByEmail={r.logged_by_email}
                        outcome={r.outcome}
                        remark={r.remark}
                        durationMin={r.duration_minutes}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CallLogDialog
        open={showLog}
        lockedFirm={firm ? { subdomain: firm.subdomain, firm_name: firm.firm_name } : null}
        lockedType="call"
        onOpenChange={(v) => { if (!v) setShowLog(false); }}
        onSaved={() => { setShowLog(false); setVersion((n) => n + 1); }}
      />
    </>
  );
}

/** Outcome icon for call logs */
function OutcomeIcon({ outcome }) {
  if (outcome === "picked_up") {
    return (
      <span title="Picked up" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
        <PhoneCall size={13} weight="fill" />
      </span>
    );
  }
  if (outcome === "no_answer") {
    return (
      <span title="Didn't pick up" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 border border-amber-200 text-amber-700">
        <PhoneSlash size={13} weight="fill" />
      </span>
    );
  }
  return <span className="text-gray-300">—</span>;
}

/** Shared row used by both Call Logs and Notes Firm-360 dialogs — 5-column
 *  layout: Time / Logged by / Outcome / Remark / Duration */
function FirmEntryRow({ time, loggedByName, loggedByEmail, outcome, remark, durationMin }) {
  const firstName = (loggedByName || "").trim().split(/\s+/)[0] || loggedByName;
  const dur = (() => {
    if (!durationMin || durationMin <= 0) return "";
    const h = Math.floor(durationMin / 60);
    const m = durationMin % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  })();
  
  return (
    <tr className="border-b border-gray-100 last:border-b-0 align-top">
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap font-mono text-xs">
        {new Date(time).toLocaleString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="text-gray-900 text-xs cursor-help" title={`${loggedByName}\n${loggedByEmail || ""}`}>
          {firstName}
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <OutcomeIcon outcome={outcome} />
      </td>
      <td className="px-3 py-2 text-gray-700 text-xs leading-relaxed">{remark}</td>
      <td className="px-3 py-2 text-right text-xs text-gray-700 font-mono whitespace-nowrap">
        {dur || <span className="text-gray-300">—</span>}
      </td>
    </tr>
  );
}
